import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { SITE } from "@/lib/site";
import { LegalStrip } from "@/components/Footer";

const LAST_UPDATE = "settembre 2026";

const LegalLayout = ({ title, subtitle, testid, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0D14] text-gray-100 flex flex-col" data-testid={testid}>
      <header className="border-b border-border bg-[#0A0D14]/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <img src="/logo.png" alt={SITE.name} className="h-9 w-auto" data-testid="legal-logo" />
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-amber-500 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
        </div>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-amber-500 mb-3">{SITE.name}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">{title}</h1>
        {subtitle && <p className="mt-3 text-sm text-gray-500">{subtitle}</p>}
        <div className="mt-10 space-y-10">{children}</div>
        <p className="mt-12 text-xs text-gray-600 font-mono2 uppercase tracking-widest">
          Ultimo aggiornamento: {LAST_UPDATE}
        </p>
      </main>

      <LegalStrip />
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="font-display text-xl font-bold uppercase tracking-tight flex items-baseline gap-3">
      <span className="text-amber-500 font-mono2 text-sm">{n}</span> {title}
    </h2>
    <div className="mt-3 text-sm text-gray-400 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Titolare = () => (
  <p>
    <strong className="text-gray-200">{SITE.name}</strong> — Sede legale: Contrada Lavanghe dell'Oglio s.n.c.,
    92011 Cattolica Eraclea (AG) — P.IVA / C.F.: 02486060847 — Tel:{" "}
    <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="text-amber-500 hover:underline">{SITE.phone}</a>{" "}
    — Email:{" "}
    <a href={`mailto:${SITE.email}`} className="text-amber-500 hover:underline">{SITE.email}</a>
  </p>
);

export const PrivacyPolicy = () => (
  <LegalLayout
    title="Privacy Policy"
    subtitle="Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR)"
    testid="privacy-policy-page"
  >
    <Section n="01" title="Titolare del trattamento">
      <Titolare />
    </Section>

    <Section n="02" title="Dati che raccogliamo">
      <p>
        <strong className="text-gray-200">Dati forniti volontariamente.</strong> Quando ci contatti tramite
        telefono, WhatsApp o email — per esempio per chiedere informazioni su un veicolo o prenotare un test
        drive — raccogliamo i dati che ci fornisci direttamente: nome, recapito telefonico e contenuto della
        richiesta.
      </p>
      <p>
        <strong className="text-gray-200">Dati di navigazione.</strong> I sistemi informatici del sito
        acquisiscono alcuni dati tecnici necessari al funzionamento (es. indirizzo IP, tipo di browser). Solo
        con il tuo consenso utilizziamo anche strumenti di analisi statistica in forma anonima e aggregata.
      </p>
    </Section>

    <Section n="03" title="Perché trattiamo i tuoi dati">
      <p>
        Trattiamo i dati personali per: rispondere alle tue richieste di informazioni o di test drive
        (art. 6, lett. b, GDPR); adempiere agli obblighi fiscali e contrattuali connessi alla vendita di
        veicoli (art. 6, lett. c, GDPR); e — solo previo consenso — analizzare in forma aggregata l'utilizzo
        del sito per migliorarne i contenuti (art. 6, lett. a, GDPR).
      </p>
      <p>I tuoi dati non vengono venduti né comunicati a terzi per finalità di marketing.</p>
    </Section>

    <Section n="04" title="Modalità e tempi di conservazione">
      <p>
        I dati sono trattati con strumenti informatici e misure di sicurezza adeguate. Li conserviamo per il
        tempo necessario a gestire la tua richiesta e, in caso di acquisto, per i tempi previsti dagli
        obblighi fiscali e di legge (in genere 10 anni per la documentazione contabile).
      </p>
    </Section>

    <Section n="05" title="I tuoi diritti">
      <p>
        In qualunque momento puoi esercitare i diritti previsti dagli articoli 15–22 del GDPR: accesso ai
        dati, rettifica, cancellazione, limitazione del trattamento, portabilità e opposizione. Puoi inoltre
        revocare il consenso prestato senza pregiudicare la liceità del trattamento precedente.
      </p>
      <p>
        Per esercitare i tuoi diritti scrivici a{" "}
        <a href={`mailto:${SITE.email}`} className="text-amber-500 hover:underline">{SITE.email}</a> o chiama
        il {SITE.phone}. Hai inoltre diritto di proporre reclamo al Garante per la protezione dei dati
        personali (www.garanteprivacy.it).
      </p>
    </Section>

    <Section n="06" title="Modifiche a questa informativa">
      <p>
        Questa informativa può essere aggiornata nel tempo. La versione vigente è sempre quella pubblicata
        su questa pagina, con l'indicazione della data di ultimo aggiornamento.
      </p>
    </Section>
  </LegalLayout>
);

export const CookiePolicy = () => (
  <LegalLayout
    title="Cookie Policy"
    subtitle="Informativa sull'uso dei cookie e delle tecnologie simili su questo sito"
    testid="cookie-policy-page"
  >
    <Section n="01" title="Cosa sono i cookie">
      <p>
        I cookie sono piccoli file di testo che i siti salvano sul tuo dispositivo. Su questo sito usiamo
        anche il "local storage" del browser, una tecnologia equivalente, per ricordare le tue preferenze.
      </p>
    </Section>

    <Section n="02" title="Cookie tecnici (sempre attivi)">
      <p>
        Sono indispensabili per il funzionamento del sito e non richiedono consenso. In particolare:
      </p>
      <ul className="list-disc pl-5 space-y-1.5">
        <li>
          <strong className="text-gray-200">Preferenza di consenso</strong> — ricorda la scelta che hai
          espresso nel banner cookie, così non te lo riproponiamo a ogni visita.
        </li>
        <li>
          <strong className="text-gray-200">Sessione area riservata</strong> — mantiene attivo l'accesso
          dell'amministratore nella sezione di gestione del catalogo.
        </li>
      </ul>
    </Section>

    <Section n="03" title="Strumenti di analisi (solo con consenso)">
      <p>
        Con il tuo consenso utilizziamo PostHog, uno strumento di analisi che ci aiuta a capire come i
        visitatori usano il sito (pagine visitate, durata della visita) in forma aggregata, per migliorare
        contenuti e servizi. Se non accetti, o se revochi il consenso, la raccolta di questi dati resta
        disattivata.
      </p>
    </Section>

    <Section n="04" title="Cookie di marketing">
      <p>
        Questo sito non installa cookie pubblicitari o di profilazione di terze parti. L'opzione "Marketing"
        nel pannello delle preferenze è disponibile per completezza, ma al momento non attiva alcuno
        strumento.
      </p>
    </Section>

    <Section n="05" title="Come gestire o revocare il consenso">
      <p>
        Puoi modificare le tue scelte in qualsiasi momento dal link{" "}
        <strong className="text-gray-200">"Gestisci cookie"</strong> presente nel piè di pagina di tutte le
        pagine del sito: si aprirà il pannello delle preferenze. Puoi inoltre eliminare i cookie dal tuo
        browser tramite le impostazioni di navigazione (Chrome, Safari, Firefox, Edge).
      </p>
    </Section>

    <Section n="06" title="Titolare del trattamento">
      <Titolare />
      <p>
        Per qualsiasi domanda su questa Cookie Policy puoi scriverci a{" "}
        <a href={`mailto:${SITE.email}`} className="text-amber-500 hover:underline">{SITE.email}</a>.
      </p>
    </Section>
  </LegalLayout>
);
